// Shared formatters for response cards and judge cards. Mirrors iOS
// JudgeCardView.formatJudgeDuration / Formatters.duration — no decimals,
// "Xm Ys" / "Ys" / "Nms" only.

export function formatDuration(ms: number | undefined | null): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return "";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

// Thousands-separator format for token counts: 9404 → "9,404".
export function formatTokenCount(n: number): string {
  return n.toLocaleString("en-US");
}

// --- Scheduled custom queries ---
// 0=Monday .. 6=Sunday (matches the server day_of_week convention).
const SCHEDULE_WEEKDAY_NAMES = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

// "07:30" (24h) → "7:30AM"; "13:05" → "1:05PM"; "00:00" → "12:00AM".
export function formatScheduleTime(hhmm: string | undefined | null): string {
  if (!hhmm) return "";
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m.toString().padStart(2, "0")}${ampm}`;
}

// A human summary of a schedule, e.g. "weekly 7:30AM Monday",
// "daily 7:30AM", "monthly 9:00AM day 15". Returns "" for a
// disabled/absent/invalid schedule. Shape is duck-typed so callers can pass
// the QuerySchedule from any of the custom-query interfaces.
export function formatScheduleSummary(schedule: {
  enabled?: boolean;
  frequency?: string;
  time_of_day?: string;
  day_of_week?: number | null;
  day_of_month?: number | null;
} | null | undefined): string {
  if (!schedule || !schedule.enabled || !schedule.frequency) return "";
  const t = formatScheduleTime(schedule.time_of_day);
  switch (schedule.frequency) {
    case "daily":
      return `daily ${t}`.trim();
    case "weekly":
      return `weekly ${t} ${SCHEDULE_WEEKDAY_NAMES[schedule.day_of_week ?? 0] ?? ""}`.trim();
    case "monthly":
      return `monthly ${t} day ${schedule.day_of_month ?? 1}`.trim();
    case "quarterly":
      return `quarterly ${t} day ${schedule.day_of_month ?? 1}`.trim();
    default:
      return "";
  }
}

// Humanize a model id like "claude-sonnet-4-6" → "Sonnet 4.6". Mirrors
// iOS JudgeCardView.humanizeModel.
export function humanizeModel(raw: string | undefined | null): string {
  if (!raw) return "";
  let s = raw;
  for (const prefix of ["claude-", "gpt-", "gemini-"]) {
    if (s.toLowerCase().startsWith(prefix)) {
      s = s.slice(prefix.length);
      break;
    }
  }
  for (const suffix of ["-pro-preview", "-preview"]) {
    if (s.toLowerCase().endsWith(suffix)) {
      s = s.slice(0, -suffix.length);
      break;
    }
  }
  const parts = s.split("-");
  if (parts.length === 0) return "";
  const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const trailing = parts.slice(1).join(".");
  return trailing ? `${first} ${trailing}` : first;
}
